import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { Link, useParams, useNavigate } from "react-router-dom";
import moment from "moment";
import Modal from "../../../Components/Modal";
import { fetchStoringById, updateStoringStatus, addNotitieToStoring } from "../Reducers/actions";

const StoringDetail = ({ 
  fetchStoringById, 
  updateStoringStatus,
  addNotitieToStoring,
  storingDetail 
}) => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [loaded, setLoaded] = useState(false);
  const [newNotitie, setNewNotitie] = useState('');
  const [showAddNotitie, setShowAddNotitie] = useState(false);
  const [statusChange, setStatusChange] = useState({
    show: false,
    newStatus: '',
    comment: ''
  });

  useEffect(() => {
    if (id) {
      fetchStoringById(id).then(() => {
        setLoaded(true);
      }).catch(() => {
        setLoaded(true);
      });
    }
  }, [id, fetchStoringById]);

  const getPriorityColor = (prioriteit) => {
    switch (prioriteit) {
      case 'kritiek': return 'danger';
      case 'hoog': return 'warning';
      case 'normaal': return 'info';
      case 'laag': return 'success';
      default: return 'secondary';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'gemeld': return 'warning';
      case 'toegewezen': return 'info';
      case 'in_progress': return 'primary';
      case 'voltooid': return 'success';
      case 'geannuleerd': return 'secondary';
      default: return 'light';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'gemeld': return 'Gemeld';
      case 'toegewezen': return 'Toegewezen';
      case 'in_progress': return 'In Progress';
      case 'voltooid': return 'Voltooid';
      case 'geannuleerd': return 'Geannuleerd';
      default: return status;
    }
  };

  const handleStatusChange = async () => {
    if (statusChange.newStatus) {
      try {
        await updateStoringStatus(id, statusChange.newStatus, statusChange.comment);
        setStatusChange({ show: false, newStatus: '', comment: '' });
        // Refresh the storing data
        fetchStoringById(id);
      } catch (error) {
        console.error('Error updating status:', error);
      }
    }
  };

  const handleAddNotitie = async () => {
    if (newNotitie.trim()) {
      try {
        await addNotitieToStoring(id, newNotitie);
        setNewNotitie('');
        setShowAddNotitie(false);
        // Refresh the storing data
        fetchStoringById(id);
      } catch (error) {
        console.error('Error adding notitie:', error);
      }
    }
  };

  if (!loaded) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="visually-hidden">Laden...</span>
            </div>
            <h6 className="text-muted">Storing wordt geladen...</h6>
          </div>
        </div>
      </div>
    );
  }

  if (!storingDetail) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <div className="text-center">
          <div className="avatar avatar-xl mb-3">
            <i className="bx bx-error-circle bx-lg text-danger"></i>
          </div>
          <h4>Storing niet gevonden</h4>
          <p className="text-muted">De opgevraagde storing kon niet worden gevonden.</p>
          <Link to="/facility/storingen" className="btn btn-primary">
            <i className="bx bx-arrow-back me-2"></i>
            Terug naar Storingen
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb">
              <li className="breadcrumb-item">
                <Link to="/facility">Facility Management</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/facility/storingen">Storingen</Link>
              </li>
              <li className="breadcrumb-item active">{storingDetail.storingsNummer}</li>
            </ol>
          </nav>
          <h4 className="fw-bold mb-2">
            Storing #{storingDetail.storingsNummer}
            <span className={`badge bg-label-${getPriorityColor(storingDetail.prioriteit)} ms-2`}>
              {storingDetail.prioriteit}
            </span>
            {storingDetail.urgentie && (
              <span className="badge bg-label-danger ms-1">
                <i className="bx bx-error"></i> Urgent
              </span>
            )}
          </h4>
        </div>
        <div>
          <Link 
            to={`/facility/storingen/${storingDetail._id}/edit`} 
            className="btn btn-outline-primary me-2"
          >
            <i className="bx bx-edit me-2"></i>Bewerk
          </Link>
          <button 
            className="btn btn-primary"
            onClick={() => setStatusChange({ ...statusChange, show: true })}
          >
            <i className="bx bx-refresh me-2"></i>Status Wijzigen
          </button>
        </div>
      </div>

      <div className="row">
        {/* Main Content */}
        <div className="col-xl-8">
          {/* Storing Details Card */}
          <div className="card mb-4">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Storing Details</h5>
              <span className={`badge bg-label-${getStatusColor(storingDetail.status)}`}>
                {getStatusLabel(storingDetail.status)}
              </span>
            </div>
            <div className="card-body">
              <div className="row mb-3">
                <div className="col-md-6">
                  <h6 className="text-muted">Titel</h6>
                  <p className="mb-0">{storingDetail.titel}</p>
                </div>
                <div className="col-md-6">
                  <h6 className="text-muted">Categorie</h6>
                  <p className="mb-0">{storingDetail.categorie?.naam || 'Onbekend'}</p>
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <h6 className="text-muted">Melder</h6>
                  <p className="mb-0">
                    {storingDetail.melder?.naam || 'Onbekend'}
                    {storingDetail.melder?.email && (
                      <small className="text-muted d-block">{storingDetail.melder.email}</small>
                    )}
                  </p>
                </div>
                <div className="col-md-6">
                  <h6 className="text-muted">Toegewezen aan</h6>
                  <p className="mb-0">
                    {storingDetail.toegewezenAan ? (
                      <>
                        {storingDetail.toegewezenAan.naam}
                        {storingDetail.toegewezenAan.email && (
                          <small className="text-muted d-block">{storingDetail.toegewezenAan.email}</small>
                        )}
                      </>
                    ) : (
                      <span className="text-muted">Niet toegewezen</span>
                    )}
                  </p>
                </div>
              </div>

              {/* Location Info */}
              {(storingDetail.accommodatie || storingDetail.kampeerplaats) && (
                <div className="row mb-3">
                  <div className="col-12">
                    <h6 className="text-muted">Locatie</h6>
                    <p className="mb-0">
                      {storingDetail.accommodatie && (
                        <>
                          <span className="badge bg-label-info me-2">Accommodatie</span>
                          {storingDetail.accommodatie.naam}
                          {storingDetail.accommodatie.beschrijving && (
                            <small className="text-muted d-block">{storingDetail.accommodatie.beschrijving}</small>
                          )}
                        </>
                      )}
                      {storingDetail.kampeerplaats && (
                        <>
                          <span className="badge bg-label-success me-2">Kampeerplaats</span>
                          {storingDetail.kampeerplaats.naam}
                          {storingDetail.kampeerplaats.beschrijving && (
                            <small className="text-muted d-block">{storingDetail.kampeerplaats.beschrijving}</small>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}

              <div className="row mb-3">
                <div className="col-md-6">
                  <h6 className="text-muted">Aangemaakt</h6>
                  <p className="mb-0">{moment(storingDetail.createdAt).format('DD-MM-YYYY HH:mm')}</p>
                </div>
                {storingDetail.slaDeadline && (
                  <div className="col-md-6">
                    <h6 className="text-muted">SLA Deadline</h6>
                    <p className={`mb-0 ${moment(storingDetail.slaDeadline).isBefore(moment()) ? 'text-danger' : 'text-muted'}`}>
                      {moment(storingDetail.slaDeadline).format('DD-MM-YYYY HH:mm')}
                      {moment(storingDetail.slaDeadline).isBefore(moment()) && (
                        <i className="bx bx-time ms-1" title="SLA overschreden"></i>
                      )}
                    </p>
                  </div>
                )}
              </div>

              <div className="mb-0">
                <h6 className="text-muted">Beschrijving</h6>
                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                  {storingDetail.beschrijving}
                </p>
              </div>
            </div>
          </div>

          {/* Photos */}
          {storingDetail.fotos && storingDetail.fotos.length > 0 && (
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0">
                  <i className="bx bx-image me-2"></i>
                  Foto's ({storingDetail.fotos.length})
                </h5>
              </div>
              <div className="card-body">
                <div className="row">
                  {storingDetail.fotos.map((foto, index) => (
                    <div key={index} className="col-md-4 mb-3">
                      <div className="card">
                        <img
                          src={`/api/facility/uploads/${foto.filename}`}
                          alt={foto.originalname}
                          className="card-img-top"
                          style={{ height: '200px', objectFit: 'cover' }}
                        />
                        <div className="card-body p-2">
                          <small className="text-muted">{foto.originalname}</small>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* History Timeline */}
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bx bx-history me-2"></i>
                Geschiedenis
              </h5>
            </div>
            <div className="card-body">
              {storingDetail.history && storingDetail.history.length > 0 ? (
                <div className="timeline">
                  {storingDetail.history.map((entry, index) => (
                    <div key={index} className="timeline-item">
                      <div className="timeline-point bg-primary"></div>
                      <div className="timeline-content">
                        <h6 className="mb-1">{entry.action.replace('_', ' ')}</h6>
                        {entry.comment && <p className="text-muted mb-1">{entry.comment}</p>}
                        <small className="text-muted">
                          {entry.user?.naam || 'Onbekend'} • {moment(entry.timestamp).format('DD-MM-YYYY HH:mm')}
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted">Geen geschiedenis beschikbaar</p>
              )}
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="col-xl-4">
          {/* Status Actions */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">Acties</h6>
            </div>
            <div className="card-body">
              <div className="d-grid gap-2">
                <button 
                  className="btn btn-primary"
                  onClick={() => setStatusChange({ ...statusChange, show: true })}
                >
                  <i className="bx bx-refresh me-2"></i>Status Wijzigen
                </button>
                <button 
                  className="btn btn-outline-secondary"
                  onClick={() => setShowAddNotitie(true)}
                >
                  <i className="bx bx-message-add me-2"></i>Notitie Toevoegen
                </button>
                <Link 
                  to={`/facility/storingen/${storingDetail._id}/edit`} 
                  className="btn btn-outline-primary"
                >
                  <i className="bx bx-edit me-2"></i>Bewerk Storing
                </Link>
              </div>
            </div>
          </div>

          {/* Quick Info */}
          <div className="card mb-4">
            <div className="card-header">
              <h6 className="mb-0">Snel Overzicht</h6>
            </div>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">Status:</span>
                <span className={`badge bg-label-${getStatusColor(storingDetail.status)}`}>
                  {getStatusLabel(storingDetail.status)}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">Prioriteit:</span>
                <span className={`badge bg-label-${getPriorityColor(storingDetail.prioriteit)}`}>
                  {storingDetail.prioriteit}
                </span>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-muted">Aangemaakt:</span>
                <span>{moment(storingDetail.createdAt).format('DD-MM HH:mm')}</span>
              </div>
              {storingDetail.slaDeadline && (
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted">SLA:</span>
                  <span className={moment(storingDetail.slaDeadline).isBefore(moment()) ? 'text-danger' : ''}>
                    {moment(storingDetail.slaDeadline).format('DD-MM HH:mm')}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Notes */}
          <div className="card">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h6 className="mb-0">Notities</h6>
              <button 
                className="btn btn-sm btn-outline-primary"
                onClick={() => setShowAddNotitie(!showAddNotitie)}
              >
                <i className="bx bx-plus"></i>
              </button>
            </div>
            <div className="card-body">
              {/* Add Notitie Form */}
              {showAddNotitie && (
                <div className="mb-3 p-3 bg-light rounded">
                  <textarea
                    className="form-control mb-2"
                    rows="3"
                    placeholder="Voeg een notitie toe..."
                    value={newNotitie}
                    onChange={(e) => setNewNotitie(e.target.value)}
                  ></textarea>
                  <div className="d-flex gap-2">
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={handleAddNotitie}
                      disabled={!newNotitie.trim()}
                    >
                      Toevoegen
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => {
                        setShowAddNotitie(false);
                        setNewNotitie('');
                      }}
                    >
                      Annuleren
                    </button>
                  </div>
                </div>
              )}

              {/* Notes List */}
              {storingDetail.notities && storingDetail.notities.length > 0 ? (
                storingDetail.notities.map((notitie, index) => (
                  <div key={index} className="mb-3 pb-3 border-bottom">
                    <p className="mb-2" style={{ whiteSpace: 'pre-wrap' }}>
                      {notitie.tekst}
                    </p>
                    <small className="text-muted">
                      {notitie.user?.naam || 'Onbekend'} • {moment(notitie.timestamp).format('DD-MM HH:mm')}
                      {notitie.intern && <span className="badge bg-label-warning ms-2">Intern</span>}
                    </small>
                  </div>
                ))
              ) : (
                <p className="text-muted mb-0">Geen notities</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Status Change Modal */}
      <Modal
        visible={statusChange.show}
        onClose={() => setStatusChange({ show: false, newStatus: '', comment: '' })}
        width={500}
        height="auto"
      >
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Status Wijzigen</h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label className="form-label">Nieuwe Status</label>
              <select 
                className="form-select"
                value={statusChange.newStatus}
                onChange={(e) => setStatusChange({ ...statusChange, newStatus: e.target.value })}
              >
                <option value="">Selecteer status</option>
                <option value="gemeld">Gemeld</option>
                <option value="toegewezen">Toegewezen</option>
                <option value="in_progress">In Progress</option>
                <option value="voltooid">Voltooid</option>
                <option value="geannuleerd">Geannuleerd</option>
              </select>
            </div>
            <div className="mb-3">
              <label className="form-label">Opmerking (optioneel)</label>
              <textarea 
                className="form-control"
                rows="3"
                value={statusChange.comment}
                onChange={(e) => setStatusChange({ ...statusChange, comment: e.target.value })}
                placeholder="Voeg een opmerking toe over de status wijziging..."
              ></textarea>
            </div>
          </div>
          <div className="card-footer d-flex justify-content-end gap-2">
            <button 
              type="button" 
              className="btn btn-outline-secondary"
              onClick={() => setStatusChange({ show: false, newStatus: '', comment: '' })}
            >
              Annuleren
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleStatusChange}
              disabled={!statusChange.newStatus}
            >
              Status Wijzigen
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

const mapStateToProps = (state) => ({
  storingDetail: state.facilityManagement?.storingDetail
});

const mapDispatchToProps = {
  fetchStoringById,
  updateStoringStatus,
  addNotitieToStoring
};

export default connect(mapStateToProps, mapDispatchToProps)(StoringDetail);